<template>
  <div class="w-full" :class="marginClass">
    <div class="bg-grey-lighter py-6 px-4 uppercase border-l-4 border-r-4 hover:border-grey" :class="subscription.data_only ? 'border-blue hover:border-blue' : (subscription.enabled ? ' border-green hover:border-green ' : '')">
      <div class="relative">
        <div class="flex items-center -mx-2">
          <div>
            <div class="flex-grow px-2">
              <!-- Checkbox -->
              <input type="checkbox" :class="checkboxClass" :id="id" v-model="subscription.enabled">
              <label :for="id">{{ subscription.label }}
                <i v-if="subscription.stared_flag && selectedOffice.network_id == 7" class="fa fa-star text-yellow-dark px-2 z-30 relative" :title="subscription.star_text"></i>
                <div class="z-30 py-1 ml-2 float-right relative" :title="subscription.book_text">
                  <icon name="book-open-regular-blue" v-if="subscription.booked_flag && selectedOffice.network_id == 7" class="w-4 h-4" :center="false" />
                </div>
              </label>
              <span v-if="subscription.type_id === APS_PROPENSITY_TO_MOVE"> - Monthly trigger </span>
              <div class="mt-3" v-if="subscription.type_id === APS_PROPENSITY_TO_MOVE">Filtered data - Anticipates homeowners likely to sell their home in the next 12 months.</div>
            </div>
            <div class="text-xs flex-grow mt-4" v-if="selectedOffice.network_id == 7">
              <toggle-input :value="subscription.data_only" :disabled="!subscription.enabled"  @toggleSwitch="toggleDataOnly" class-name="mt-2" label-on="Print" label-off="Data Only"></toggle-input>
            </div>
          </div>
          <div class="px-2 flex-grow w-24">
            <div class="relative">
              <!-- Template Preview -->
              <iframe class="iframe" :srcdoc="templateHtml" :class="{invisible: !subscription.enabled}" ref="templateFrame"></iframe>
              <i class="fas fa-sync fa-spin fa-fw float-right" v-if="loading"></i>
              <div v-else-if="templateHtml" :class="{invisible: !subscription.enabled}">
                <div class="iframe iframe-cover" @click="showModal">
                </div>
                <i class="fas status-icon border" :class="(subscription.enabled && !subscription.product_id) || (subscription.enabled && outOfAreaEnabled && !subscription.out_of_area.product_id) ? 'fa-exclamation-triangle bg-red-dark text-white border-red-dark' : 'fa-sync text-primary bg-white border-grey'" @click="showModal"></i>
              </div>
              <i v-else-if="subscription.product_id" class="status-icon fas fa-exclamation-triangle text-red-dark" @click="showModal"></i>
            </div>
          </div>
          <div class="px-2 flex-shrink" v-if="subscription.type_id === APS_PROPENSITY_TO_MOVE && subscription.enabled">
            <div class="bg-white flex items-center flex-col justify-center text-center p-2 rounded">
              <icon name="propensity" class="w-8 h-8 mb-2" :center="false" />
              Next Data Delivery
              <strong class="mt-1"> {{ nextDeliveryDate }} </strong>
            </div>
          </div>
        </div>

        <!-- Modal opener -->
        <div class="absolute pin cursor-pointer z-10" v-if="!subscription.enabled && totalSubCount>0" @click="enableSubscription"></div>
      </div>
    </div>

    <!-- Modal -->
    <aps-modal @close-modal="templatePickerOpen = false" :subscription="subscription" :department="department" :out-of-area-enabled="outOfAreaEnabled" v-if="templatePickerOpen" />
    <modal @leave="deleteTriggerModalOpen = false; subscription.enabled = true" v-if="deleteTriggerModalOpen" show-close-btn class="text-center text-sm leading-normal">
      <p class="mb-4"><b>Please note:</b> Deactivating the trigger point will delete any associated pending print items.</p>
      <button @click="saveSubscription(); deleteTriggerModalOpen = false"
          class="bg-primary rounded py-3 px-12 text-white">
        <span>Deactivate</span>
      </button>
    </modal>
    <modal @leave="limitCheckModalOpen = false" v-if="limitCheckModalOpen" show-close-btn class="text-center text-sm leading-normal">
      <p><b>Please note:</b> that you are unable to remove all of your triggers.<br />
        If you wish to switch off APS, please contact: <a :href="`mailto:${system.support_email}`">{{ system.support_email }}</a></p>
    </modal>
  </div>
</template>

<script>
import { propertyLogicApi as api } from '@/http'
import { mapState, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import srcDoc from 'srcdoc-polyfill'
import {
  APS_PROPENSITY_TO_MOVE
} from '@/consts'

export default {
  name: 'apsTrigger',
  props: {
    meta: Object,
    subscription: Object,
    department: String,
    className: String,
    outOfAreaEnabled: Boolean,
    totalSubCount: Number,
    nextDeliveryDate: String
  },
  data () {
    return {
      templateHtml: null,
      templatePickerOpen: false,
      skipSave: false,
      loading: false,
      limitCheckModalOpen: false,
      deleteTriggerModalOpen: false,
      checkboxClass: 'green'
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'system']),
    id: function () {
      return 'apsTrigger' + this.department + this.subscription.type_id
    },
    marginClass: function () {
      let output = this.subscription.type_id === 7 ? 'mb-3' : 'mb-1'

      if (this.className) {
        output += ' ' + this.className
      }

      if (this.subscription.type_id !== APS_PROPENSITY_TO_MOVE) {
        output += ' px-2 lg:w-1/2'
      }

      return output
    }
  },
  methods: {
    ...mapActions(['displayToastNotification', 'setPageLoading']),
    enableSubscription () {
      if (this.subscription.product_id) {
        this.loading = true
        api.get(`/aps/triggers/template/${this.selectedOffice.id}/${this.subscription.product_id}`).then((response) => {
          if (this.outOfAreaEnabled) {
            this.subscription.out_of_area.extent = response.data.maxSort
          }
          this.subscription.extent = response.data.maxSort
          this.subscription.enabled = true
          this.loading = false
        }, () => {
          this.loading = false
        })
        return
      }

      this.showModal()
    },
    saveSubscription () {
      this.setPageLoading(true)
      api.patch('/aps/triggers/save-subscription', {
        office_id: this.selectedOffice.id,
        meta: this.meta,
        department: this.department,
        subscription: this.subscription
      }).then((response) => {
        if (response.data.last_print === true) {
          let message = this.subscription.enabled ? 'Automated print is active for this office. You must have at least one trigger with print enabled.'
            : 'You must have one active trigger, contact Support to cancel your subscription'
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Print Data Error',
            info: message
          })
          this.subscription.data_only = false
          this.subscription.enabled = true
          this.checkboxClass = 'green'
        } else if (this.subscription.id === null) {
          this.skipSave = true
          this.subscription.id = response.data.id
        }
        this.setPageLoading(false)
      }, () => {
        // Error
        this.setPageLoading(false)
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong. Changes have not been saved.'
        })
      })
    },
    showModal: function () {
      this.templatePickerOpen = true
    },
    limitCheck: function () {
      if (this.totalSubCount === 1) {
        this.limitCheckModalOpen = true
      }
    },
    loadTemplate () {
      if (!this.subscription.product_id) {
        return
      }

      this.loading = true

      api.get(`/aps/triggers/template/${this.selectedOffice.id}/${this.subscription.product_id}`).then((response) => {
        this.templateHtml = `<!DOCTYPE HTML><html><head><style>html,body{padding:0;margin:0;overflow:hidden}${response.data.css}</style></head><body>${response.data.markup}</body></html>`
        srcDoc.set(this.$refs.templateFrame)
        this.loading = false
      }, () => {
        this.loading = false
      })
    },
    toggleDataOnly () {
      this.subscription.data_only = !this.subscription.data_only
      if (this.subscription.data_only) {
        this.checkboxClass = 'blue'
      } else {
        this.checkboxClass = 'green'
      }
    }
  },
  created () {
    this.loadTemplate()
    if (this.subscription.data_only) {
      this.checkboxClass = 'blue'
    }
  },
  watch: {
    'subscription.product_id': {
      handler: function () {
        this.loadTemplate()
      }
    },
    subscription: {
      handler: debounce(function () {
        if (this.skipSave) {
          this.skipSave = false
          return
        }
        if (!this.subscription.enabled) {
          this.deleteTriggerModalOpen = true
          this.subscription.data_only = false
          return
        }

        this.saveSubscription()
      }, 500),
      deep: true
    }
  }
}
</script>

<style type="scss" scoped>
iframe {
  pointer-events: none;
}
.iframe {
  position: absolute;
  right: -85px;
  top: 50%;
  width: 245px;
  height: 173px;
  transform: translateY(-50%) scale(.2);
}
.iframe-cover {
  z-index: 1;
  cursor: pointer;
}
.status-icon {
  padding: 3px;
  border-radius: 50%;
  position: absolute;
  right: 5px;
  bottom: 7px;
  font-size: 8px;
  z-index: 2;
  cursor: pointer;
}

input[type="checkbox"].blue:checked + label::before {
   background-color: #3490DC;
 }
</style>
